'use client';

import React, { useEffect } from 'react';
import { useUser } from '../../hooks/useUser';
import { useUserConsents } from '../../hooks/useUserConsents';
import { setAppAnalyticsConsent } from '../../utils/analytics/appAnalyticsConsent';
import { setAppsFlyerCustomerUserId } from '../../utils/analytics/appsflyerAnalytics';
import { hashId } from '../../utils/utils';

/**
 * Keeps the native analytics services (Clarity recording and Firebase Analytics
 * collection) in step with the user's backend app-analytics consent (contract 8),
 * which is the source of truth.
 *
 * Mounted once (native only). Because it reacts to the cached consent value it
 * covers every path: the initial load — so a device that never toggled locally
 * (fresh install, cleared storage) still honours a previously stored choice — as
 * well as optimistic toggles and rollbacks.
 *
 * Fail-closed: while there is no logged-in user (anonymous, or after logout) the
 * services are disabled. They are only enabled once a logged-in user's consent
 * has loaded and is granted.
 *
 * It also owns the AppsFlyer Customer User ID: the (hashed) user id is set only when a
 * logged-in user has granted consent, and cleared on logout or when consent is revoked —
 * consistent with the identifying-param stripping applied to forwarded events.
 */
export const AppAnalyticsConsentSync: React.FC = () => {
  const { user } = useUser();
  const { appAnalyticsConsent, isLoading } = useUserConsents();

  useEffect(() => {
    // Wait for the consent query to resolve so we don't briefly disable services
    // for a logged-in user whose consent is still loading.
    if (isLoading) return;
    const enabled = !!user && appAnalyticsConsent;
    void setAppAnalyticsConsent(enabled);
    void setAppsFlyerCustomerUserId(enabled && user ? String(hashId(user.userId)) : null);
  }, [user, isLoading, appAnalyticsConsent]);

  return null;
};
